import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType } from '@classification-testing/domain';
import type { ClassificationDialogData, ClassificationContentType, ClassificationDialogLabel, ClassificationDialogPolicy } from '@core/domain';

/**
 * Maps ContentType enum to ClassificationContentType string.
 */
function mapContentType(contentType: string): ClassificationContentType {
  const normalized = contentType?.toLowerCase();

  switch (normalized) {
    case 'audio':
      return 'audio';
    case 'image':
      return 'image';
    case 'text':
      return 'text';
    case 'username':
      return 'username';
    case 'mixed':
      return 'mixed';
    default:
      return 'text';
  }
}

/**
 * Maps classification labels to the canonical format.
 */
function mapLabels(labels: ClassificationResult['labels']): ClassificationDialogLabel[] {
  return labels.map(label => ({
    name: label.label_name,
    severity: label.severity,
    shortName: label.label_short_name,
    confidence: label.confidence,
    contentType: label.content_type,
    score: label.score,
    labelId: label.label_id,
  }));
}

/**
 * Maps policies to the canonical format.
 */
function mapPolicies(policies: ClassificationResult['policies']): ClassificationDialogPolicy[] | undefined {
  if (!policies || policies.length === 0) return undefined;

  return policies.map(policy => ({
    id: policy.id,
    name: policy.name,
    description: policy.description,
    labelIds: policy.labels?.map(l => l.label_id),
  }));
}

/**
 * Converts a ClassificationResult from classification-testing domain
 * to the canonical ClassificationDialogData type used by the core dialog.
 */
export function toClassificationDialogData(result: ClassificationResult): ClassificationDialogData {
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;

  return {
    id: result.id,
    contentType: mapContentType(result.contentType),
    isSafe,
    text: result.text,
    contentUrl: result.contentUrl,
    labels: mapLabels(result.labels || []),
    confidenceScore: result.confidenceScore,
    language: result.languageDetected || result.language,
    timestamp: result.timestamp,
    duration: result.duration,
    explanation: result.explanation,
    triggeredPolicies: mapPolicies(result.policies),
    nonTriggeredPolicies: mapPolicies(result.nonTriggeredPolicies),
    // User info not available in classification testing context
    userInfo: undefined,
    topicName: undefined,
    eventType: 'Classification',
  };
}
